import { useEffect, useRef, useState, type CSSProperties } from "react";
import { easeInOut } from "motion/react";

import { cn } from "@/lib/utils";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import { useMediaQuery } from "@/hooks/use-media-query";

interface LightRaysProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
  count?: number;
  color?: string;
  blur?: number;
  speed?: number;
  length?: string;
  mobileCount?: number;
}

type LightRay = {
  left: number;
  rotate: number;
  width: number;
  swing: number;
  delay: number;
  duration: number;
  intensity: number;
};

// Rays are soft, so their pre-blurred sprites are stored at half resolution
// and scaled up when drawn; the result is indistinguishable from full size.
const SPRITE_SCALE = 0.5;

const createRays = (count: number, cycle: number): LightRay[] => {
  if (count <= 0) return [];

  return Array.from({ length: count }, () => {
    const left = 8 + Math.random() * 84;
    const rotate = -28 + Math.random() * 56;
    const width = 160 + Math.random() * 160;
    const swing = 0.8 + Math.random() * 1.8;
    const delay = Math.random() * cycle;
    const duration = cycle * (0.75 + Math.random() * 0.5);
    const intensity = 0.6 + Math.random() * 0.5;

    return { left, rotate, width, swing, delay, duration, intensity };
  });
};

function toRgba(color: string): [number, number, number, number] {
  const ctx = document.createElement("canvas").getContext("2d")!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  return [r!, g!, b!, a! / 255];
}

/** A ray as it would look as a blurred, rounded gradient element. */
function bakeRay(
  width: number,
  height: number,
  blur: number,
  [r, g, b, a]: [number, number, number, number]
) {
  const pad = Math.ceil(blur * 3);
  const canvas = new OffscreenCanvas(
    Math.ceil((width + pad * 2) * SPRITE_SCALE),
    Math.ceil((height + pad * 2) * SPRITE_SCALE)
  );
  const ctx = canvas.getContext("2d")!;
  ctx.scale(SPRITE_SCALE, SPRITE_SCALE);
  // Filter lengths ignore the context's transform, so scale the blur too.
  ctx.filter = `blur(${blur * SPRITE_SCALE}px)`;
  // Matches `from-[color-mix(color 70%, transparent)] to-transparent`.
  const gradient = ctx.createLinearGradient(0, pad, 0, pad + height);
  gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${a * 0.7})`);
  gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.roundRect(pad, pad, width, height, width / 2);
  ctx.fill();
  // An immutable bitmap stays resident as a texture between frames.
  return { sprite: canvas.transferToImageBitmap(), pad };
}

/** Rotation and opacity of a ray `elapsed` seconds into the animation. */
function sway(ray: LightRay, elapsed: number, motionEnabled: boolean) {
  const dim = ray.intensity * 0.5;
  if (!motionEnabled || elapsed < ray.delay) {
    return { rotate: ray.rotate, opacity: dim };
  }

  // Out and back over `duration`, then a hold for a tenth of it. Each half
  // eases in and out, as Motion does for keyframes.
  const cycle = ray.duration * 1.1;
  const phase = ((elapsed - ray.delay) % cycle) / ray.duration;
  const low = ray.rotate - ray.swing;
  const high = ray.rotate + ray.swing;
  if (phase >= 1) return { rotate: low, opacity: dim };

  const outward = phase < 0.5;
  const t = easeInOut(outward ? phase * 2 : (phase - 0.5) * 2);
  const k = outward ? t : 1 - t;
  return {
    rotate: low + (high - low) * k,
    opacity: dim + (ray.intensity - dim) * k,
  };
}

export function LightRays({
  className,
  style,
  count = 7,
  color = "rgba(160, 210, 255, 0.2)",
  blur = 36,
  speed = 14,
  length = "70vh",
  mobileCount = 3,
  ref,
  ...props
}: LightRaysProps) {
  const [rays, setRays] = useState<LightRay[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lengthProbeRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const hasHardwareAcceleration = useHardwareAcceleration();
  const cycleDuration = Math.max(speed, 0.1);
  const motionEnabled = !reduceMotion && hasHardwareAcceleration && !isMobile;
  const effectiveCount =
    isMobile || !motionEnabled ? Math.min(count, mobileCount) : count;

  useEffect(() => {
    setRays(createRays(effectiveCount, cycleDuration));
  }, [effectiveCount, cycleDuration]);

  // All rays are drawn into one canvas from pre-blurred sprites. As separate
  // blurred, blended elements, every frame of their sway had to be
  // re-composited layer by layer. Screen blending is associative, so
  // screening the rays together here and then screening the canvas over the
  // glow gives the same result.
  useEffect(() => {
    const canvas = canvasRef.current;
    const probe = lengthProbeRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !probe || !ctx || rays.length === 0) return;

    const rgba = toRgba(color);
    let width = 0;
    let height = 0;
    // Not 0, so sprites get baked on the first resize even at zero height.
    let rayLength = -1;
    let sprites: ReturnType<typeof bakeRay>[] = [];
    let frameId: number | null = null;
    const start = performance.now();

    const draw = (now: number) => {
      const elapsed = (now - start) / 1000;
      ctx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = "screen";

      rays.forEach((ray, index) => {
        const { sprite, pad } = sprites[index]!;
        const { rotate, opacity } = sway(ray, elapsed, motionEnabled);
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.translate((ray.left / 100) * width, height * -0.12);
        ctx.rotate((rotate * Math.PI) / 180);
        ctx.drawImage(
          sprite,
          -ray.width / 2 - pad,
          -pad,
          ray.width + pad * 2,
          rayLength + pad * 2
        );
        ctx.restore();
      });
    };

    const resize = () => {
      const nextLength = probe.offsetHeight;
      if (nextLength !== rayLength) {
        rayLength = nextLength;
        sprites.forEach(({ sprite }) => sprite.close());
        sprites = rays.map((ray) => bakeRay(ray.width, rayLength, blur, rgba));
      }
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      draw(performance.now());
    };

    const loop = (now: number) => {
      draw(now);
      frameId = window.requestAnimationFrame(loop);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    observer.observe(probe);
    resize();
    if (motionEnabled) frameId = window.requestAnimationFrame(loop);

    return () => {
      observer.disconnect();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      sprites.forEach(({ sprite }) => sprite.close());
    };
  }, [blur, color, motionEnabled, rays]);

  if (effectiveCount === 0) return null;

  return (
    <div
      aria-hidden="true"
      ref={ref}
      className={cn(
        "pointer-events-none absolute inset-0 isolate overflow-hidden rounded-[inherit]",
        className
      )}
      style={
        {
          "--light-rays-color": color,
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={
            {
              background:
                "radial-gradient(circle at 20% 15%, color-mix(in srgb, var(--light-rays-color) 45%, transparent), transparent 70%)",
            } as CSSProperties
          }
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={
            {
              background:
                "radial-gradient(circle at 80% 10%, color-mix(in srgb, var(--light-rays-color) 35%, transparent), transparent 75%)",
            } as CSSProperties
          }
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 size-full mix-blend-screen"
        />
        {/* Resolves `length` (any CSS length) to pixels for the canvas. */}
        <div
          ref={lengthProbeRef}
          className="invisible absolute w-0"
          style={{ height: length }}
        />
      </div>
    </div>
  );
}
